import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Alert, AlertDescription, ExcelUploadDialog } from '@bliks-io/ui';
import {
  EditableDraftTable,
  type DraftColumnDef,
  type RowDeleteAction
} from './EditableDraftTable';
import { type DraftRowShape, type RowValidator } from '@/lib/draft-rows/draftRow';
import type { DuplicateCheck } from '@/lib/draft-rows/markDuplicateRows';
import { DraftTableToolbar } from './DraftTableToolbar';
import type { ClearScope } from './ClearDraftsButton';
import { DraftErrorAlert } from './DraftErrorAlert';
import { DraftSurface } from './DraftSurface';
import type { ExplainerSection } from './FieldExplainer';
import { WizardStepHeader } from '@/components/wizard/WizardStepHeader';
import { setupDoneNotice, type DoneNoticeKeys } from './setupDoneNotice';

interface DraftTableStepProps<TData, TRow extends DraftRowShape<TData>> {
  /** Resolved step title (steps translate before passing) */
  title: string;
  description?: string;
  hint?: string;
  isDone?: boolean;
  doneNoticeKeys?: DoneNoticeKeys;

  /** Rows to display — a done anchor step shows seeds until the first edit */
  rows: TRow[];
  onRowsChange: (rows: TRow[]) => void;
  columns: DraftColumnDef<TData>[];
  validateRow: RowValidator<TData>;
  duplicateChecks: Array<DuplicateCheck<TData>>;
  makeEmptyRow: (rowNumber: number) => TRow;
  addLabelKey: string;
  /** i18n key of the table card header */
  titleKey: string;

  /**
   * The underlying draft state when it differs from `rows` (a done anchor step
   * displays seeds until the first edit). Drives the clear button, the invalid
   * count and the error banner from the real edits rather than the seed
   * preview. Defaults to `rows` for steps without seeding.
   */
  draftRows?: TRow[];

  /** What "Liste leeren" removes — picks the confirm dialog copy */
  clearScope?: ClearScope;
  /** Per-row delete affordance (see RowDeleteAction). Defaults to 'enabled'. */
  deleteAction?: (row: TRow) => RowDeleteAction;
  /**
   * The table was cleared with imported rows still queued for deletion —
   * Weiter commits them, so the empty state carries a destructive notice.
   */
  pendingDeleteAll?: boolean;

  /** Resolved dialog title */
  uploadTitle: string;
  /** i18n key of the empty state's "what we need per row" heading */
  explainerHeadingKey: string;
  /** Field explainer shown in the empty state, one entry per template column */
  explainerSections: ExplainerSection[];
  downloadTemplate: () => void;
  /** Parses the file into the step's draft state; returns the parsed row count */
  parse: (file: File) => Promise<number>;
  /** Further import buttons beside the Excel upload, in the toolbar and the empty state */
  importActions?: ReactNode;

  submitError?: string;

  /** Extra content under the table, only while rows exist (e.g. a mail note) */
  footer?: ReactNode;

  /**
   * Overrides the empty ⇆ table branch (default: no rows). The users step
   * forces the empty state while only protected members exist, so the upload
   * CTA stays visible until the first NEW row.
   */
  isEmpty?: boolean;
  /** Rendered under the empty state only (forwarded to DraftSurface) */
  emptyExtras?: ReactNode;
  /** Replaces the explainer empty state; gets the upload dialog and the manual row */
  emptyState?: (actions: { openUpload: () => void; addRow: () => void }) => ReactNode;
}

/**
 * The shared scaffold behind the wizard's four flat draft steps (Departments,
 * Users, Systems, Processes): header, error banner, the upload dialog with its
 * open state, and the empty-state ⇆ editable-table branch with its toolbar.
 * Each step supplies only its columns, parser and entity-specific handlers.
 * The tabbed Funktionen/Teams step keeps its own layout and reuses the pieces
 * (DraftErrorAlert) directly.
 */
export function DraftTableStep<TData, TRow extends DraftRowShape<TData>>({
  title,
  description,
  hint,
  isDone = false,
  doneNoticeKeys,
  rows,
  onRowsChange,
  columns,
  validateRow,
  duplicateChecks,
  makeEmptyRow,
  addLabelKey,
  titleKey,
  draftRows = rows,
  clearScope = 'drafts',
  deleteAction,
  pendingDeleteAll = false,
  uploadTitle,
  explainerHeadingKey,
  explainerSections,
  downloadTemplate,
  parse,
  importActions,
  submitError,
  footer,
  isEmpty = rows.length === 0,
  emptyExtras,
  emptyState
}: DraftTableStepProps<TData, TRow>) {
  const { t } = useTranslation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  // Append instead of replace: a forced-empty users step may hold
  // uncommitted protected placement edits in the draft that a reset
  // would silently discard.
  const addRow = () => onRowsChange([...draftRows, makeEmptyRow(draftRows.length + 1)]);

  const invalidRowCount = draftRows.filter((row) => !row.isValid).length;

  const table = (
    <EditableDraftTable
      rows={rows}
      onRowsChange={onRowsChange}
      columns={columns}
      validateRow={validateRow}
      duplicateChecks={duplicateChecks}
      addLabelKey={addLabelKey}
      makeEmptyRow={makeEmptyRow}
      titleKey={titleKey}
      deleteAction={deleteAction}
      toolbar={
        <DraftTableToolbar
          // Displayed rows, not drafts: on an untouched done step the seeds
          // are on screen and "Liste leeren" must offer to delete them too.
          canClear={rows.length > 0}
          onClear={() => onRowsChange([])}
          onUpload={() => setIsUploadOpen(true)}
          clearScope={clearScope}
          importActions={importActions}
        />
      }
    />
  );

  // A footer note (e.g. the Processes mail hint) sits under the table only
  // while rows exist.
  const tableBranch = footer ? (
    <div>
      {table}
      {footer}
    </div>
  ) : (
    table
  );

  return (
    <div className="space-y-6 px-10 pb-6 pt-9">
      <WizardStepHeader
        title={title}
        description={description}
        hint={hint}
        isDone={isDone}
        {...setupDoneNotice(t, doneNoticeKeys)}
      />

      <DraftErrorAlert submitError={submitError} invalidRowCount={invalidRowCount} />

      {pendingDeleteAll && (
        <Alert variant="destructive">
          <Trash2 className="h-4 w-4" />
          <AlertDescription>{t('initialSetup.pendingDeleteAll')}</AlertDescription>
        </Alert>
      )}

      <DraftSurface
        isEmpty={isEmpty}
        headingKey={explainerHeadingKey}
        sections={explainerSections}
        downloadTemplate={downloadTemplate}
        parse={parse}
        onAddManually={addRow}
        addLabelKey={addLabelKey}
        importActions={importActions}
        emptyState={emptyState?.({ openUpload: () => setIsUploadOpen(true), addRow })}
        emptyExtras={emptyExtras}
      >
        {tableBranch}
      </DraftSurface>

      <ExcelUploadDialog
        open={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        title={uploadTitle}
        downloadTemplate={downloadTemplate}
        parse={parse}
      />
    </div>
  );
}
